import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { AlertCircle } from 'lucide-react';

interface Step3Props {
    jobDescription: string;
    onBack: () => void;
}

const Step3: React.FC<Step3Props> = ({ jobDescription, onBack }) => {
    const navigate = useNavigate();
    const [isPublishing, setIsPublishing] = useState(false);
    const [message, setMessage] = useState('');

    const textToPDF = async (text: string, filename: string) => {
        const doc = new jsPDF();
        doc.text(text, 10, 10);
        const blob = doc.output('blob');
        const timestamp = new Date().toISOString().replace(/[-:.]/g, '');
        const timestampedFilename = filename.replace('.pdf', `_${timestamp}.pdf`);
        return new File([blob], timestampedFilename, { type: 'application/pdf' });
    };

    const handlePublish = async () => {
        setIsPublishing(true);
        setMessage('');

        try {
            const pdfFile = await textToPDF(jobDescription, 'Job_Description.pdf');
            const formData = new FormData();
            formData.append('jobDescription', pdfFile);

            const response = await fetch('/api/job_description_adder', {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error('Failed to publish job description');
            }

            setMessage('Job Description published successfully!');
            const successMessage = atob("Your job description has been posted!")
            setTimeout(() => navigate('/dashboard&message=' + successMessage), 2000);
        } catch (error) {
            setMessage('Error publishing job description');
            console.error('Publish error:', error);
        } finally {
            setIsPublishing(false);
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-lg font-medium">Final Job Description</h2>
                <p className="mt-1 text-sm text-gray-500">
                    Review your final job description before publishing.
                </p>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
                <div className="prose max-w-none">
                    <div className="whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: jobDescription }}></div>
                </div>
            </div>

            {message && (
                <div className={`flex items-center gap-2 p-4 rounded-lg ${message.includes('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                    }`}>
                    <AlertCircle className="h-5 w-5" />
                    <p className="text-sm">{message}</p>
                </div>
            )}

            <div className="flex space-x-4">
                <button
                    onClick={onBack}
                    className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                    Back
                </button>
                <button
                    onClick={handlePublish}
                    disabled={isPublishing}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isPublishing ? (
                        <div className="flex items-center gap-2">
                            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Publishing...
                        </div>
                    ) : (
                        'Publish Job Description'
                    )}
                </button>
            </div>
        </div>
    );
};

export default Step3;